import { API_ROUTES_FOR_AXIOS } from '../constants/ApiRoutes';
import { axiosInstance } from '../constants/axiosInstance';
import { ApiResponse, PaginatedResponse } from 'src/types/commonResponseTypes';
import {
  DeleteUsersApiResponse,
  DeleteUsersPayload,
  FindUsersByOrgIdPayload,
  UpdateUserActiveStatus,
  UpdateUserResponse,
  UpdateUserRole,
  UserApiResponse,
} from 'src/types/user';
import { CheckOnboardingCompleteApiResponse } from 'src/types/organization';
import { UpdateAdultConsentPayload } from 'src/types/hanaUseCases';
import { handleError } from 'src/utils/commonErrorUtils';

const normalizeUserConsentFields = (
  user?: UserApiResponse | null,
): UserApiResponse | undefined => {
  if (!user) {
    return undefined;
  }

  const adultConsentConfirmed = user.adultConsentConfirmed ?? false;

  return {
    ...user,
    adultConsentConfirmed,
  };
};

export const getUserInfoByCookie = async (): Promise<
  ApiResponse<UserApiResponse>
> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['getUserInfoByCookie']();
    const { data } = await axiosInstance.get(apiUrl);

    return {
      ...data,
      content: normalizeUserConsentFields(data.content),
    };
  } catch (error) {
    return handleError(error);
  }
};

export const validateMongoUserId = async (
  userId: string,
): Promise<ApiResponse<boolean>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['validateMongoUserId'](userId);
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getAllUsers = async <T>(
  page: number,
  limit: number,
  search?: string,
  payload?: FindUsersByOrgIdPayload,
): Promise<ApiResponse<PaginatedResponse<T>>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['getAllUsers'](page, limit, search);

    const { data } = await axiosInstance.post(apiUrl, payload);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const getAllActiveOrganizationUsers = async (
  payload?: FindUsersByOrgIdPayload,
): Promise<UserApiResponse[]> => {
  const limit = 100;
  const first = await getAllUsers<UserApiResponse[]>(
    1,
    limit,
    undefined,
    payload,
  );

  if (!first.success || !first.content) {
    throw new Error(first.message || 'Workspace members could not be loaded.');
  }

  const rows = [...first.content.data];
  const pageCount = Math.ceil(first.content.totalCount / limit);

  for (let page = 2; page <= pageCount; page += 1) {
    const response = await getAllUsers<UserApiResponse[]>(
      page,
      limit,
      undefined,
      payload,
    );

    if (!response.success || !response.content) {
      throw new Error(
        response.message || 'Workspace members could not be loaded.',
      );
    }

    rows.push(...response.content.data);
  }

  return Array.from(
    new Map(rows.map((member) => [member._id, member])).values(),
  ).filter((member) => member.isActive !== false);
};

export const updateUserActiveStatus = async (
  body: UpdateUserActiveStatus,
): Promise<ApiResponse<UpdateUserResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['updateUserActiveStatus']();
    const { data } = await axiosInstance.patch(apiUrl, body);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateUserRole = async (
  body: UpdateUserRole,
): Promise<ApiResponse<UpdateUserResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['updateUserRole']();
    const { data } = await axiosInstance.patch(apiUrl, body);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const deleteUsersByIds = async (
  deleteUsersPayload: DeleteUsersPayload,
): Promise<ApiResponse<DeleteUsersApiResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['deleteUsers']();
    const { data } = await axiosInstance.delete(apiUrl, {
      data: deleteUsersPayload,
    });

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const checkOnboardingComplete = async (): Promise<
  ApiResponse<CheckOnboardingCompleteApiResponse>
> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['checkOnboardingComplete']();
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const completeRevampOnboarding = async (): Promise<
  ApiResponse<CheckOnboardingCompleteApiResponse>
> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['completeRevampOnboarding']();
    const { data } = await axiosInstance.post(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateAdultConsent = async (
  payload: UpdateAdultConsentPayload,
): Promise<ApiResponse<UserApiResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['updateAdultConsent']();

    const { data } = await axiosInstance.patch(apiUrl, {
      adultConsentConfirmed: payload.adultConsentConfirmed,
    });

    return {
      ...data,
      content: normalizeUserConsentFields(data.content),
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getEmailPreferencesLink = async (): Promise<
  ApiResponse<{ sent: boolean }>
> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['getEmailPreferencesLink']();
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateUserTimezone = async (payload: {
  timezone: string;
}): Promise<ApiResponse<UserApiResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['updateUserTimezone']();
    const { data } = await axiosInstance.patch(apiUrl, payload);

    return {
      ...data,
      content: normalizeUserConsentFields(data.content),
    };
  } catch (error) {
    return handleError(error);
  }
};
